import { jsx as _jsx } from "react/jsx-runtime";
/* eslint-disable no-console */
/* eslint-disable no-prototype-builtins */
import { useEffect } from "react";
import { datadogRum } from "@datadog/browser-rum";
import { datadogLogs } from "@datadog/browser-logs";
import { ErrorBoundary, useErrorBoundary } from "react-error-boundary";
import axios from "axios";
import { findOwnerForRoute } from "../../utils/utils";
const blackboxFeContext = new Map();
const workerUrl = new URL("../../worker/datadog-browser-worker-4.50.1.js", import.meta.url).href;
const setBlackboxFeContextProperty = (key, value) => {
    blackboxFeContext.set(key, value);
};
const getBlackboxFeContextProperty = (key) => blackboxFeContext.get(key);
const getBlackboxFeContext = () => blackboxFeContext;
function isNetworkError(e) {
    if (e instanceof TypeError &&
        [
            "The network connection was lost.", // Safari
            "Failed to fetch", // Chrome
            "NetworkError when attempting to fetch resource.", // Firefox
            "Network request failed", // polyfill from https://github.com/github/fetch
        ].includes(e.message)) {
        return true;
    }
    return false;
}
export const getVariantFromError = (error) => {
    const { status } = error.response || {};
    if (status === 401 || status === 403 || status === 404 || status === 500) {
        return String(status);
    }
    else if (error.name === "WayflyerError") {
        return error.variant;
    }
    else
        return "unknown";
};
const FallbackComponentWrapper = ({ error, resetErrorBoundary, children }) => {
    const variant = getVariantFromError(error);
    return children({ error, resetErrorBoundary, variant });
};
const getErrorContext = (context) => {
    var _a, _b;
    const viewPathsOwners = (_a = getBlackboxFeContextProperty("viewPathsOwners")) !== null && _a !== void 0 ? _a : {};
    const displayOwners = (_b = getBlackboxFeContextProperty("displayOwners")) !== null && _b !== void 0 ? _b : {};
    const { pathname, hash } = window.location;
    const { owner, displayOwner } = findOwnerForRoute({
        ownersMap: viewPathsOwners,
        pathname,
        hash,
        displayOwnersMap: displayOwners,
    });
    return (context === null || context === void 0 ? void 0 : context.hasOwnProperty("wf.blackbox.owner"))
        ? Object.assign(Object.assign({}, context), { "wf.blackbox.displayOwner": displayOwners[owner] }) : Object.assign(Object.assign({}, context), { "wf.blackbox.owner": owner, "wf.blackbox.displayOwner": displayOwner });
};
const addWarning = (message, context, error) => {
    const isProd = sessionStorage.getItem("blackbox-fe-is-prod");
    const errorContext = getErrorContext(context);
    if (isProd) {
        logger.warn(message, errorContext, error);
    }
    else {
        console.log("[BlackBox dev warning log]", { message, context, error });
    }
};
const addError = (error, context) => {
    const isProd = sessionStorage.getItem("blackbox-fe-is-prod");
    const errorContext = getErrorContext(context);
    isProd
        ? (() => {
            !isNetworkError(error) && datadogRum.addError(error, errorContext);
        })()
        : console.log("[BlackBox dev error log]", { error, context });
};
const is4XXAxiosError = (error) => { var _a; return typeof ((_a = error.response) === null || _a === void 0 ? void 0 : _a.status) === "number" && error.response.status >= 400 && error.response.status < 500; };
const addAction = (message, action) => {
    const isProd = sessionStorage.getItem("blackbox-fe-is-prod");
    isProd
        ? (() => {
            datadogRum.addAction(message, action);
        })()
        : console.log("[BlackBox dev action log]", { message, action });
};
const initialise = (config) => {
    const { datadog: datadogConfig, viewPathsOwners, displayOwners = {} } = config;
    setBlackboxFeContextProperty("viewPathsOwners", viewPathsOwners);
    setBlackboxFeContextProperty("displayOwners", displayOwners);
    const isProd = datadogConfig.env === "prod";
    if (isProd) {
        sessionStorage.setItem("blackbox-fe-is-prod", "true");
    }
    datadogRum.init(Object.assign(Object.assign({}, datadogConfig), { workerUrl, defaultPrivacyLevel: "mask-user-input" }));
    datadogRum.startSessionReplayRecording();
    isProd &&
        datadogLogs.init(Object.assign(Object.assign({}, datadogConfig), { sessionSampleRate: 100, forwardConsoleLogs: ["warn", "error"], forwardErrorsToLogs: true, telemetrySampleRate: 100, useSecureSessionCookie: true }));
};
const BlackBox = ({ children, user, FallbackComponent }) => {
    var _a, _b;
    const { pathname, hash } = window.location;
    const viewPathsOwners = (_a = getBlackboxFeContextProperty("viewPathsOwners")) !== null && _a !== void 0 ? _a : {};
    const displayOwners = (_b = getBlackboxFeContextProperty("displayOwners")) !== null && _b !== void 0 ? _b : {};
    useEffect(() => {
        if (user) {
            datadogRum.setUser(user);
            datadogLogs.setUser(user);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const handleError = (error, info) => {
        const { owner, displayOwner } = findOwnerForRoute({
            ownersMap: viewPathsOwners,
            pathname,
            hash,
            displayOwnersMap: displayOwners,
        });
        const context = Object.assign(Object.assign({}, info), { "wf.blackbox.owner": owner, "wf.blackbox.displayOwner": displayOwner });
        if (axios.isAxiosError(error) && is4XXAxiosError(error)) {
            addWarning(error.message, context, error);
        }
        else {
            addError(error, context);
        }
    };
    const handleReset = () => window.location.assign(`${window.location.protocol}//${window.location.hostname}/`);
    return (_jsx(ErrorBoundary, { onError: handleError, onReset: handleReset, FallbackComponent: ({ error, resetErrorBoundary }) => (_jsx(FallbackComponentWrapper, { error: error, resetErrorBoundary: resetErrorBoundary, children: ({ error, resetErrorBoundary, variant }) => (_jsx(FallbackComponent, { error: error, resetErrorBoundary: resetErrorBoundary, variant: variant })) })), children: children }));
};
const { setUser, setUserProperty, setGlobalContext, setGlobalContextProperty, setRumGlobalContext, clearUser, removeUserProperty, } = datadogRum;
const { logger: DatadogLogger } = datadogLogs;
const logger = typeof window !== "undefined" && window.sessionStorage.getItem("blackbox-fe-is-prod") === "true"
    ? DatadogLogger
    : console;
export { initialise, BlackBox, getBlackboxFeContext, getBlackboxFeContextProperty, setBlackboxFeContextProperty, useErrorBoundary, addAction, addError, setUser, setGlobalContext, setGlobalContextProperty, setRumGlobalContext, clearUser, setUserProperty, removeUserProperty, logger, is4XXAxiosError, addWarning, };
